import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../theme';
import DashboardLayout from '../../../../components/dashboard/DashboardLayout';
import { Card, CardHeader, CardTitle, CardBody } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Table } from '../../../../components/ui/Table';
import { Modal } from '../../../../components/ui/Modal';
import { useAuth } from '../../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiSearch, FiEye, FiEdit, FiX, FiPlus } from 'react-icons/fi';
import {
  fetchFactoryClients,
  fetchLocations,
  updateFactoryClient,
  createFactoryClient,
  formatDate,
  FactoryClientData,
  LocationData,
} from '../service/factoryClientService';
import { auth } from '../../../../firebase/config';
import { Select } from '../../../../components/ui/Select';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const SearchInputContainer = styled.div`
  flex: 1;
  min-width: 250px;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${theme.colors.light.background.tertiary};
  color: ${theme.colors.light.text.secondary};
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

const FactoryClientPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [clients, setClients] = useState<FactoryClientData[]>([]);
  const [filtered, setFiltered] = useState<FactoryClientData[]>([]);
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<FactoryClientData | null>(null);
  const [updatedClient, setUpdatedClient] = useState<FactoryClientData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check permissions
  const isStaffUser = !!staffData;
  const canViewClients = isStaffUser
    ? (staffData?.access?.clients === true)
    : !!companyData; // Admin/company owner can always view

  const canAddClients = isStaffUser
    ? (staffData?.access?.clients === true && (staffData?.permissions?.clients === 'add' || staffData?.permissions?.clients === 'edit' || staffData?.permissions?.clients === 'delete'))
    : !!companyData; // Admin/company owner can always add

  const canEditClients = isStaffUser
    ? (staffData?.access?.clients === true && (staffData?.permissions?.clients === 'edit' || staffData?.permissions?.clients === 'delete'))
    : !!companyData; // Admin/company owner can always edit

  // Create form state
  const [createFormData, setCreateFormData] = useState({
    industryName: '',
    clientName: '',
    mobile: '',
    address: '',
    locationId: '',
    dateOfRegistration: null as Date | null,
    requiredGuard: 3,
    contactName: '',
    contactEmail: '',
    contactNumber: '',
    salary: 0,
  });

  // Use company ID from context if available, otherwise fall back to authenticated user UID
  const companyId = companyData?.id || auth.currentUser?.uid || '';

  console.log('🏢 FactoryClientPage - Company ID being used:', companyId);
  console.log('   companyData?.id:', companyData?.id);
  console.log('   user?.uid:', user?.uid);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, companyData]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsData, locationsData] = await Promise.all([
        fetchFactoryClients(companyId),
        fetchLocations(),
      ]);
      setClients(clientsData);
      setFiltered(clientsData);
      setLocations(locationsData);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFiltered(clients);
      return;
    }
    const results = clients.filter((c) =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFiltered(results);
  };

  const openViewModal = (client: FactoryClientData) => {
    setSelectedClient(client);
    setUpdatedClient({ ...client });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleFieldChange = (field: keyof FactoryClientData, value: any) => {
    setUpdatedClient((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setUpdatedClient(selectedClient ? { ...selectedClient } : null);
  };

  const saveUpdate = async () => {
    if (!updatedClient || !selectedClient) return;

    if (!updatedClient.name?.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!updatedClient.industryName?.trim()) {
      toast.error('Industry name is required');
      return;
    }

    setLoading(true);
    try {
      await updateFactoryClient(
        updatedClient.id,
        updatedClient.industryId,
        updatedClient,
        companyId
      );
      toast.success('Client updated successfully');
      await loadData();
      setIsEditing(false);
      setShowModal(false);
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(error.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const getLocationName = (locationId?: string) => {
    if (!locationId) return 'N/A';
    const loc = locations.find((l) => l.id === locationId);
    return loc ? loc.name : 'N/A';
  };

  const handleCreateClient = async () => {
    if (!createFormData.industryName.trim()) {
      toast.error('Industry name is required');
      return;
    }
    if (!createFormData.clientName.trim()) {
      toast.error('Client name is required');
      return;
    }
    if (!createFormData.locationId) {
      toast.error('Location is required');
      return;
    }

    setLoading(true);
    try {
      await createFactoryClient(
        createFormData.industryName,
        createFormData.clientName,
        createFormData.mobile,
        createFormData.address,
        createFormData.locationId,
        createFormData.dateOfRegistration,
        companyId,
        createFormData.requiredGuard,
        createFormData.contactName,
        createFormData.contactEmail,
        createFormData.contactNumber,
        createFormData.salary
      );
      toast.success('Factory client created successfully!');
      setShowCreateModal(false);
      setCreateFormData({
        industryName: '',
        clientName: '',
        mobile: '',
        address: '',
        locationId: '',
        dateOfRegistration: null,
        requiredGuard: 3,
        contactName: '',
        contactEmail: '',
        contactNumber: '',
        salary: 0,
      });
      await loadData();
    } catch (error: any) {
      console.error('Create error:', error);
      toast.error(error.message || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if user doesn't have access
  if (!canViewClients) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>
            <EmptyState>
              <EmptyStateText>You don't have permission to view factory clients.</EmptyStateText>
            </EmptyState>
          </CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Factory Clients</PageTitle>
        {canAddClients && (
          <Button onClick={() => setShowCreateModal(true)}>
            <FiPlus size={18} />
            Add Factory Client
          </Button>
        )}
      </PageHeader>

      <SearchContainer>
        <SearchInputContainer>
          <Input
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            dark
          />
        </SearchInputContainer>
        <Button onClick={handleSearch}>
          <FiSearch size={18} />
          Search
        </Button>
        {searchTerm && (
          <Button variant="secondary" onClick={() => {
            setSearchTerm('');
            setFiltered(clients);
          }}>
            <FiX size={18} />
            Reset
          </Button>
        )}
      </SearchContainer>

      {loading && clients.length === 0 ? (
        <LoadingContainer>
          <Spinner />
          <p style={{ color: theme.colors.light.text.secondary }}>Loading clients...</p>
        </LoadingContainer>
      ) : filtered.length === 0 ? (
        <EmptyState>
          <p>No clients found</p>
        </EmptyState>
      ) : (
        <Card>
          <CardBody>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Industry</Table.HeaderCell>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Mobile</Table.HeaderCell>
                  <Table.HeaderCell>Address</Table.HeaderCell>
                  <Table.HeaderCell>Client Code</Table.HeaderCell>
                  <Table.HeaderCell>Action</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.map((client) => (
                  <Table.Row key={client.id}>
                    <Table.Cell>{client.industryName}</Table.Cell>
                    <Table.Cell>{client.name}</Table.Cell>
                    <Table.Cell>{client.mobile || 'N/A'}</Table.Cell>
                    <Table.Cell>{client.address || 'N/A'}</Table.Cell>
                    <Table.Cell>
                      <Badge>{client.securityCode || 'N/A'}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Button size="sm" variant="primary" onClick={() => openViewModal(client)}>
                        <FiEye size={18} />
                        View
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </CardBody>
        </Card>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? 'Edit Client' : updatedClient?.name || 'Client Details'}
        footer={
          <>
            {!isEditing ? (
              <>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Close
                </Button>
                {canEditClients && (
                  <Button variant="primary" onClick={startEditing}>
                    <FiEdit size={18} />
                    Edit
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={cancelEditing}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={saveUpdate} disabled={loading}>
                  Update
                </Button>
              </>
            )}
          </>
        }
      >
        {updatedClient && (
          <div>
            <FormGroup>
              <Input
                label="Industry"
                value={updatedClient.industryName || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('industryName', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Name"
                value={updatedClient.name || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                dark
                fullWidth
                required
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Mobile"
                value={updatedClient.mobile || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('mobile', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Address"
                value={updatedClient.address || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Input
                  label="Contact Name"
                  value={updatedClient.contactName || ''}
                  readOnly={!isEditing}
                  onChange={(e) => handleFieldChange('contactName', e.target.value)}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Contact Email"
                  value={updatedClient.contactEmail || ''}
                  readOnly={!isEditing}
                  onChange={(e) => handleFieldChange('contactEmail', e.target.value)}
                  dark
                  fullWidth
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="Contact Number"
                value={updatedClient.contactNumber || ''}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <label
                style={{
                  display: 'block',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                }}
              >
                Date of Registration
              </label>
              <Input
                label="Date of Registration"
                type="date"
                value={
                  updatedClient.dateOfRegistration
                    ? typeof updatedClient.dateOfRegistration.toDate === 'function'
                      ? updatedClient.dateOfRegistration.toDate().toISOString().split('T')[0]
                      : updatedClient.dateOfRegistration instanceof Date
                        ? updatedClient.dateOfRegistration.toISOString().split('T')[0]
                        : new Date(updatedClient.dateOfRegistration).toISOString().split('T')[0]
                    : ''
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (isEditing) {
                    handleFieldChange('dateOfRegistration', e.target.value ? new Date(e.target.value) : null);
                  }
                }}
                readOnly={!isEditing}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Required Guards"
                type="number"
                value={String(updatedClient.requiredGuard || 3)}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('requiredGuard', parseInt(e.target.value) || 3)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Salary (Rs.)"
                type="number"
                value={String(updatedClient.salary || 0)}
                readOnly={!isEditing}
                onChange={(e) => handleFieldChange('salary', parseFloat(e.target.value) || 0)}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Select
                label="Location"
                value={updatedClient.locationId || ''}
                disabled={!isEditing}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFieldChange('locationId', e.target.value)}
                dark
                fullWidth
              >
                <option value="">Select Location</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </div>
        )}
      </Modal>

      {/* Create Factory Client Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add Factory Client"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateClient} disabled={loading}>
              {loading ? 'Creating...' : 'Create Client'}
            </Button>
          </>
        }
      >
        <FormGroup>
          <Input
            label="Industry Name *"
            value={createFormData.industryName}
            onChange={(e) => setCreateFormData({ ...createFormData, industryName: e.target.value })}
            placeholder="e.g., Textile, Manufacturing"
            required
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Client Salary (Rs.)"
            type="number"
            value={String(createFormData.salary || 0)}
            onChange={(e) => setCreateFormData({ ...createFormData, salary: parseFloat(e.target.value) || 0 })}
            placeholder="Salary for guards"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Client Name *"
            value={createFormData.clientName}
            onChange={(e) => setCreateFormData({ ...createFormData, clientName: e.target.value })}
            placeholder="Enter client name"
            required
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Mobile"
            value={createFormData.mobile}
            onChange={(e) => setCreateFormData({ ...createFormData, mobile: e.target.value })}
            placeholder="03xx-xxxxxxx"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Address"
            value={createFormData.address}
            onChange={(e) => setCreateFormData({ ...createFormData, address: e.target.value })}
            placeholder="Full address"
            fullWidth
            dark
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Input
              label="Contact Name"
              value={createFormData.contactName}
              onChange={(e) => setCreateFormData({ ...createFormData, contactName: e.target.value })}
              placeholder="Enter contact person name"
              fullWidth
              dark
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="Contact Email"
              value={createFormData.contactEmail}
              onChange={(e) => setCreateFormData({ ...createFormData, contactEmail: e.target.value })}
              placeholder="contact@example.com"
              fullWidth
              dark
            />
          </FormGroup>
        </FormRow>
        <FormGroup>
          <Input
            label="Contact Number"
            value={createFormData.contactNumber}
            onChange={(e) => setCreateFormData({ ...createFormData, contactNumber: e.target.value })}
            placeholder="03xx-xxxxxxx"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Date of Registration"
            type="date"
            value={
              createFormData.dateOfRegistration
                ? createFormData.dateOfRegistration.toISOString().split('T')[0]
                : ''
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCreateFormData({
                ...createFormData,
                dateOfRegistration: e.target.value ? new Date(e.target.value) : null,
              })
            }
            dark
            fullWidth
          />
        </FormGroup>
        <FormGroup>
          <Input
            label="Required Guards"
            type="number"
            value={String(createFormData.requiredGuard)}
            onChange={(e) => setCreateFormData({ ...createFormData, requiredGuard: parseInt(e.target.value) || 3 })}
            placeholder="Number of guards"
            fullWidth
            dark
          />
        </FormGroup>
        <FormGroup>
          <Select
            label="Location *"
            value={createFormData.locationId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCreateFormData({ ...createFormData, locationId: e.target.value })}
            dark
            fullWidth
            required
          >
            <option value="">Select Location</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </Select>
        </FormGroup>
      </Modal>
    </DashboardLayout>
  );
};

export default FactoryClientPage;
